/**
 * Wix Headless API client configuration.
 *
 * Reads credentials from environment variables. The public site and the
 * Sentinel Portal connect to the SAME Wix Headless backend, so these keys
 * are shared across both surfaces.
 *
 * Required env vars (set in .env):
 *   VITE_WIX_CLIENT_ID        – Wix OAuth app client id
 *   VITE_WIX_API_KEY          – Wix API key (for server-side / headless calls)
 *   VITE_WIX_SITE_ID          – Wix site id
 *   VITE_WIX_ACCOUNT_ID       – Wix account id (optional, needed for some APIs)
 */

export interface WixConfig {
  clientId: string;
  apiKey: string;
  siteId: string;
  accountId: string;
  baseUrl: string;
  configured: boolean;
}

function readEnv(key: string): string {
  // Vite exposes VITE_* vars via import.meta.env
  const val = (import.meta.env[key] as string | undefined) ?? "";
  return val.trim();
}

export const wixConfig: WixConfig = {
  clientId: readEnv("VITE_WIX_CLIENT_ID"),
  apiKey: readEnv("VITE_WIX_API_KEY"),
  siteId: readEnv("VITE_WIX_SITE_ID"),
  accountId: readEnv("VITE_WIX_ACCOUNT_ID"),
  baseUrl: "https://www.wixapis.com",
  configured:
    readEnv("VITE_WIX_CLIENT_ID").length > 0 &&
    readEnv("VITE_WIX_API_KEY").length > 0,
};

/**
 * Standard headers for Wix Headless REST calls.
 */
export function wixHeaders(extra: Record<string, string> = {}): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: wixConfig.apiKey,
    "wix-site-id": wixConfig.siteId,
    "wix-client-id": wixConfig.clientId,
    ...extra,
  };
}

/**
 * Generic Wix REST request with graceful error handling.
 * Returns `null` when Wix is not configured so the UI can fall back to
 * placeholder / seeded content without crashing.
 */
export async function wixFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T | null> {
  if (!wixConfig.configured) {
    return null;
  }
  try {
    const res = await fetch(`${wixConfig.baseUrl}${path}`, {
      ...options,
      headers: wixHeaders(
        options.headers ? { ...((options.headers as Record<string, string>) || {}) } : {},
      ),
    });
    if (!res.ok) {
      console.warn(`[wix] ${path} returned ${res.status}`);
      return null;
    }
    return (await res.json()) as T;
  } catch (err) {
    console.warn(`[wix] ${path} failed`, err);
    return null;
  }
}
